import {Button, Spinner, Text} from "@nike/eds";
import React, {useContext, useState} from "react";
import {edsSpace16} from "@nike/eds/dist/tokens";
import {BalancingResultsTable} from "./BalancingResultsTable";
import {CustomsInventoryCountTable} from "./CustomsInventoryCountTable";
import "./DownloadableReports.css";
import {CaretRight} from "@nike/nike-design-system-icons";
import {useNavigate} from "react-router-dom";
import SnackbarContext from "../../providers/snackbar-context";
import useSWR from "swr";

type InventoryCount = {
    ELC: any[];
    GENK: any[];
    TRANSIT: any[];
    DIVERTS_IN_TRANSIT: any[];
};

export function DownloadableReports({inventoryService}) {

    const snackbarCtx = useContext(SnackbarContext);
    const [generatingReport, setGeneratingReport] = useState(false);
    const fetcher = async () => {
        try {
            const response = await inventoryService.getInventoryReports();
            return response.data.getInventoryReports;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
            snackbarCtx.displayMsg(errorMessage, "error");
            throw error;
        }
    };

    const { data, isLoading } = useSWR("inventoryReports", fetcher, {
        refreshInterval: 15000
    });

    const reportS3Bucket = data?.reportS3Bucket;
    const reportRegion = data?.reportRegion;
    const stockReports = data?.stockReports || [];
    const stockContainerBolReports = data?.stockContainerBolReports || [];
    const balancingReports = data?.balancingReports
        ?.filter((report: { location: string; }) => report.location === "ELC")
        ?.flatMap((report: { reports: any; }) => report.reports) ?? [];

    const inventoryCount: InventoryCount = {
        ELC: [],
        GENK: [],
        TRANSIT: [],
        DIVERTS_IN_TRANSIT: []
    };

    const inventoryContainerBolCount: InventoryCount = {
        ELC: [],
        GENK: [],
        TRANSIT: [],
        DIVERTS_IN_TRANSIT: []
    };

    stockReports.forEach((entry) => {
        if (inventoryCount[entry.location as keyof InventoryCount]) {
            inventoryCount[entry.location as keyof InventoryCount] = (entry.reports || []).slice(0, 4);
        }
    });

    stockContainerBolReports.forEach((entry) => {
        if (inventoryContainerBolCount[entry.location as keyof InventoryCount]) {
            inventoryContainerBolCount[entry.location as keyof InventoryCount] = (entry.reports || []).slice(0, 4);
        }
    });

    const titleStyle = { marginBottom: edsSpace16 };
    const buttonStyle = { marginBottom: edsSpace16, cursor: "pointer" };

    const navigate = useNavigate();
    const routeChange = () => navigate('/inventory-overview');

    const createInventoryReport = async () => {
        setGeneratingReport(true);
        await inventoryService
            .submitCreateInventoryReport()
            .then(() => {
                snackbarCtx.displayMsg("Successfully triggered report generation. Be patient and refresh / come back in a couple of minutes.", "success");
            })
            .catch((error: { message: any; }) => {
                snackbarCtx.displayMsg(error.message, "error");
            })
            .finally(() => {
                setGeneratingReport(false);
            });
    };

    return (
        <div>
            <Text style={titleStyle} font={"display-3"} as={"h3"}>DOWNLOADABLE REPORTS</Text>
            <Button style={buttonStyle} variant={"ghost"} onClick={() => routeChange()}>
                Inventory
                <CaretRight size={"s"}/>
            </Button>

            <div className="eds-spacing--mb-24">
                <div>
                    <Text style={titleStyle} font={"title-5"} as={"h5"}>Balancing Results</Text>
                </div>
                <BalancingResultsTable
                    balancingReports={balancingReports}
                    reportS3Bucket={reportS3Bucket}
                    reportRegion={reportRegion}
                    loading={isLoading}
                />
            </div>
            <div>
                {generatingReport ? (
                    <Spinner size="medium"/>
                ) : (<Button style={buttonStyle} onClick={createInventoryReport}>Generate Inventory Reports</Button>
                    )}
            </div>
            <div className="eds-spacing--mb-24">
                <div>
                    <Text style={titleStyle} font={"title-5"} as={"h5"}>Actual Customs Inventory Count</Text>
                </div>
                <CustomsInventoryCountTable
                    loading={isLoading}
                    inventoryCount={inventoryCount}
                    reportS3Bucket={reportS3Bucket}
                    reportRegion={reportRegion}
                />
            </div>

            <div className="eds-spacing--mb-24">
                <div>
                    <Text style={titleStyle} font={"title-5"} as={"h5"}>Actual Customs Inventory Count (with container/bol)</Text>
                </div>
                <CustomsInventoryCountTable
                    loading={isLoading}
                    inventoryCount={inventoryContainerBolCount}
                    reportS3Bucket={reportS3Bucket}
                    reportRegion={reportRegion}
                />
            </div>
        </div>
    );
}